import React, { useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useStore, useSelector, shallowEqual } from 'react-redux';
import { useAnimation, motion } from 'motion/react';
import { getEntitlements, getUserFavorites } from '@surfline/web-common';

import { fetchCollections } from 'actions/multiCam';
import SurfCamsTags from 'components/multi-cams/SurfCamsTags';
import CollectionList from 'components/multi-cams/CollectionList';
import CollectionRail from 'components/multi-cams/CollectionRail';
import CompanionAd from 'components/multi-cams/CompanionAd';
import MultiCam from 'components/multi-cams/MultiCam';
import PageRail from 'components/PageRail';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { usePageCall } from 'hooks/usePageCall';
import useFullscreen from 'hooks/useFullscreen';
import { useToggle } from 'hooks/useToggle';
import { useFramerAnimation } from 'hooks/useFramerAnimation';
import { useReduxAction } from 'hooks/useReduxAction';
import { useKeyPress } from 'hooks/useKeyPress';
import userSettingsPropType from 'propTypes/userSettings';
import { getActiveCollection } from 'selectors/multiCam';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppDispatch } from 'stores/hooks';
import { getIsPremiumWithAdsUser } from 'utils/user';

import styles from './SurfCams.module.scss';

const getClass = (isFullscreen, rightRailIsOpen, showDesktopAd, showMobileAd) =>
  classNames({
    'sl-multi-cam-page': true,
    'sl-multi-cam-page--fullscreen': isFullscreen,
    'sl-multi-cam-page--right-rail': rightRailIsOpen,
    'sl-multi-cam-page--desktop-ad': showDesktopAd,
    'sl-multi-cam-page--mobile-ad': showMobileAd,
  });

const SurfCams = ({
  collectionsLoading,
  settings,
  doSetSelectedSpots,
  selectedSpots,
  primarySpotIndex,
  doDecrementPrimarySpotIndex,
  doIncrementPrimarySpotIndex,
}) => {
  const store = useStore();
  const state = store.getState();
  const dispatch = useAppDispatch();
  const favorites = getUserFavorites(state);
  const activeCollection = getActiveCollection(state);
  if (!activeCollection) {
    dispatch(fetchCollections(favorites));
  }
  const { isDesktop } = useDeviceSize();
  const { hasAdFreePermissions, hasMultiCamPermissions } = useUserPermissionStatus();
  const entitlements = useSelector(getEntitlements, shallowEqual);
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);
  const properties = useRef({ category: 'cams & reports' });
  usePageCall('multi-cam', properties?.current);
  const multiCamRef = useRef(null);
  const [isFullscreen, toggleFullscreen] = useFullscreen(multiCamRef);

  const [rightRailIsOpen, _, toggleRightRail] = useToggle(false);

  const [leftRailIsOpen, __, toggleLeftRail] = useToggle(true);
  const [isMounted, setIsMounted] = useState(false);

  const showRightRail = !collectionsLoading && !!activeCollection;
  const leftRailSpinnerControls = useAnimation();
  const rightRailControls = useAnimation();
  const leftRailControls = useAnimation();
  const [startAnimations] = useFramerAnimation({
    controls: leftRailSpinnerControls,
    variant: 'hidden',
    start: showRightRail,
  });
  useFramerAnimation({
    controls: rightRailControls,
    variant: 'loaded',
    start: activeCollection?.fetchedSpots && !activeCollection?.loading,
    reset: activeCollection?._id,
  });
  const [leftRailAnimationComplete] = useFramerAnimation({
    controls: leftRailControls,
    variant: 'shown',
    start: startAnimations,
  });

  const shouldResetSelectedSpots =
    !!activeCollection && !collectionsLoading && activeCollection?.fetchedSpots;
  useReduxAction({
    action: doSetSelectedSpots,
    memoizedArgs: useMemo(() => [activeCollection], [activeCollection]),
    condition: shouldResetSelectedSpots,
  });
  /** Keyboard Event Listeners (Vim Keybinds for Jason Wang) */
  useKeyPress(doDecrementPrimarySpotIndex, 'ArrowLeft', 'h', 'H');
  useKeyPress(doIncrementPrimarySpotIndex, 'ArrowRight', 'l', 'L');
  useKeyPress((key) => toggleLeftRail(key === '['), '[', ']');

  const canMountCams = isMounted && leftRailAnimationComplete;

  const showAd = useMemo(() => {
    if (isPremiumWithAdsUser) return true;
    if (hasAdFreePermissions || hasMultiCamPermissions) return false;
    return true;
  }, [isPremiumWithAdsUser, hasAdFreePermissions, hasMultiCamPermissions]);

  const showDesktopAd = isMounted && isDesktop && showAd;
  const showMobileAd = isMounted && !isDesktop && showAd;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div className={styles.surfCams} data-testid="surf-cams">
      <div className={getClass(isFullscreen, rightRailIsOpen, showDesktopAd, showMobileAd)}>
        <SurfCamsTags activeCollection={activeCollection} />
        <PageRail open={leftRailIsOpen && !isFullscreen} side="left">
          <CollectionList
            activeCollection={activeCollection}
            leftRailControls={leftRailControls}
            leftRailIsOpen={leftRailIsOpen}
            toggleLeftRail={toggleLeftRail}
            spinnerControls={leftRailSpinnerControls}
          />
        </PageRail>
        <MultiCam
          canMountCams={canMountCams}
          isFullscreen={isFullscreen}
          multiCamRef={multiCamRef}
          rightRailIsOpen={rightRailIsOpen}
          toggleFullscreen={toggleFullscreen}
          toggleRightRail={toggleRightRail}
        >
          {showMobileAd && (
            <CompanionAd adConfigName="sl-multi-cam-companion-mobile" entitlements={entitlements} />
          )}
        </MultiCam>
        <PageRail side="right" open={rightRailIsOpen && !isFullscreen}>
          <motion.div className="sl-collection-rail-container">
            <CollectionRail
              settings={settings}
              activeCollection={activeCollection}
              primarySpotId={selectedSpots[primarySpotIndex] && selectedSpots[primarySpotIndex]._id}
              selectedSpots={selectedSpots}
              collectionControls={rightRailControls}
            />
          </motion.div>
          {showDesktopAd && (
            <CompanionAd adConfigName="sl-multi-cam-companion" entitlements={entitlements} />
          )}
        </PageRail>
      </div>
    </div>
  );
};

SurfCams.propTypes = {
  selectedSpots: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      cameras: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ).isRequired,
  doSetSelectedSpots: PropTypes.func.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  settings: userSettingsPropType.isRequired,
  primarySpotIndex: PropTypes.number.isRequired,
  doDecrementPrimarySpotIndex: PropTypes.func.isRequired,
  doIncrementPrimarySpotIndex: PropTypes.func.isRequired,
};

export default SurfCams;
